import React from "react";
import TenantLogo from "../layout/components/TenantLogo";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { RootState } from "../../redux/store";
import { useSelector } from "react-redux";
import Header from "../layout/components/Header";
import CloseIcon from "../../components/icons/CloseIcon";
import { Button } from "../../components/ui/button";
import BookLocationHeader from "../bookLocation/common/util/BookLocationHeader";
import IdeaSubmissionForm from "./IdeaSubmissionForm";
import { isSharedMode } from "../../util/tenantConfig";

function WithEmail({ text, email }: { text: string; email: string }) {
  const parts = text.split("{email}");

  // No tenant contact e-mail configured: render the sentence without the
  // placeholder rather than an empty `mailto:` link. An unset `contact.email`
  // used to produce <a href="mailto:"></a> — a failure that looked like an
  // absence. The inline form below is the route that still works.
  if (!email) {
    return <>{parts.join("")}</>;
  }

  return (
    <>
      {parts[0]}
      <a
        href={`mailto:${email}`}
        className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm"
      >
        {email}
      </a>
      {parts[1]}
    </>
  );
}

const MoreToComeContent = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { config } = useSelector((state: RootState) => state.tenantConfig);

  const redirectToRoot = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Header>
        <div className="h-12"></div>
        <TenantLogo />
        <Button
          variant="ghost"
          size="icon"
          onClick={redirectToRoot}
          aria-label={t("common.close", { defaultValue: "Close" })}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full text-foreground/90"
        >
          <CloseIcon />
        </Button>
      </Header>

      <section className="flex-1">
        <div className="max-w-[1000px] mx-auto px-4">
          <BookLocationHeader
            text={
              config?.content?.[i18n.language]?.moreToCome?.header_text || ""
            }
          />
          <div className="mt-6">
            <p className="text-center text-base tracking-[0.32px] leading-6 text-[#515557] mb-6">
              {config?.content?.[i18n.language]?.moreToCome?.first_text}
            </p>
            <p className="text-center text-base tracking-[0.32px] leading-6 text-[#515557] mb-6">
              <WithEmail
                text={
                  config?.content?.[i18n.language]?.moreToCome?.second_text ||
                  ""
                }
                email={config?.email || ""}
              />
            </p>
            {/* Only runtime (shared-deployment) tenants get the inline form:
                their store is operational and carries the idea_submission
                table. The pinned bundled builds (vonovia/dhu/sup) resolve to
                legacy per-tenant databases where the endpoint answers 501 —
                showing their users a form that can never succeed would be a
                shipped-but-stranded UI (#1361 shape); they keep mailto-only.
                Should one of those tenants migrate stores, its build moves to
                the shared deployment and the form appears by itself. */}
            {isSharedMode() && <IdeaSubmissionForm />}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MoreToComeContent;
